import React, { useState } from "react";
import { Tag, Button, Modal, Select, message, Space } from "antd";
import { useHistory } from "react-router-dom";
import facturaApi from "../../modules/admin/facturas/store/api";
import request from "../../helper/request";
import "./FacturaInfo.scss";

const { Option } = Select;

const FacturaInfo = ({ route, isAdmin = false, onRouteUpdated }) => {
  const history = useHistory();
  const [showAddToFacturaModal, setShowAddToFacturaModal] = useState(false);
  const [unpaidFacturas, setUnpaidFacturas] = useState([]);
  const [selectedFacturaId, setSelectedFacturaId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);

  // Function to get client ID directly from the API
  const getClientIdForRoute = async (routeId) => {
    try {
      const endpoint = isAdmin
        ? `/admin/routes/${routeId}/client-id`
        : `/routes/${routeId}/client-id`;
      const response = await request.get(endpoint);
      return response.clientId;
    } catch (error) {
      console.error("Failed to fetch client ID for route:", routeId, error);
      return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "green";
      case "partial":
        return "gold";
      case "unpaid":
        return "default";
      case "overdue":
        return "red";
      default:
        return "default";
    }
  };

  const getBackgroundColor = (status) => {
    switch (status) {
      case "paid":
        return "#f6ffed"; // pastel green
      case "partial":
        return "#fffbe6"; // pastel yellow
      case "unpaid":
        return "#e6f7ff"; // pastel blue
      case "overdue":
        return "#fff2f0"; // light red
      default:
        return "#f5f5f5"; // grey
    }
  };

  const formatFacturaId = (facturaId) => {
    if (!facturaId) return "";
    // Split after date and dash, but before short client id and increment
    // e.g. "2025-09-03-abcd1234-01" becomes "2025-09-03-\nabcd1234-01"
    const match = facturaId.match(/^(\d{4}-\d{2}-\d{2}-)(.*)/);
    if (match) {
      return (
        <>
          {match[1]}
          <br />
          {match[2]}
        </>
      );
    }
    return facturaId;
  };

  const handleFacturaClick = () => {
    if (route?.factura?.id) {
      const facturaPath = isAdmin
        ? `/admin/facturas/edit/${route.factura.id}`
        : `/billing/facturas/${route.factura.id}`;
      history.push(facturaPath);
    }
  };

  const handleNewFactura = async () => {
    if (!route?.id) {
      message.error("Route ID not available");
      return;
    }

    setCreatingNew(true);
    try {
      // Get client ID directly from API
      const clientId = await getClientIdForRoute(route.id);
      if (!clientId) {
        message.error("Unable to determine client for this route");
        setCreatingNew(false);
        return;
      }

      const newFactura = await facturaApi.createFacturaWithRoutes(clientId, [route.id]);
      message.success("New factura created successfully!");

      // Call callback to refresh route data
      if (onRouteUpdated) {
        onRouteUpdated();
      }

      // Navigate to the new factura
      history.push(`/admin/facturas/edit/${newFactura.id}`);
    } catch (error) {
      console.error("Create factura error:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Unknown error";
      message.error("Failed to create new factura: " + errorMessage);
    } finally {
      setCreatingNew(false);
    }
  };

  const handleShowAddToFactura = async () => {
    if (!route?.id) {
      message.error("Route ID not available");
      return;
    }

    setLoading(true);
    try {
      // Get client ID directly from API
      const clientId = await getClientIdForRoute(route.id);
      if (!clientId) {
        message.error("Unable to determine client for this route");
        setLoading(false);
        return;
      }

      const response = await facturaApi.getUnpaidFacturas(clientId);
      const facturas = response.data || [];

      if (facturas.length === 0) {
        message.info(
          "No unpaid facturas found for this client. Create a new factura instead."
        );
        return;
      }

      setUnpaidFacturas(facturas);
      setShowAddToFacturaModal(true);
    } catch (error) {
      console.error("Load facturas error:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Unknown error";
      message.error("Failed to load unpaid facturas: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToExistingFactura = async () => {
    if (!selectedFacturaId) {
      message.error("Please select a factura");
      return;
    }

    setLoading(true);
    try {
      await facturaApi.addRoutesToFactura(selectedFacturaId, [route.id]);
      message.success("Route added to factura successfully!");

      // Call callback to refresh route data
      if (onRouteUpdated) {
        onRouteUpdated();
      }

      setShowAddToFacturaModal(false);
      setSelectedFacturaId(null);

      // Navigate to the updated factura
      history.push(`/admin/facturas/edit/${selectedFacturaId}`);
    } catch (error) {
      console.error("Add to factura error:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Unknown error";
      message.error("Failed to add route to factura: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    setShowAddToFacturaModal(false);
    setSelectedFacturaId(null);
  };

  const hasFactura = route?.factura?.id;
  const backgroundColor = hasFactura
    ? getBackgroundColor(route.factura.status)
    : getBackgroundColor(null);

  const cursor = hasFactura ? "pointer" : "default";

  return (
    <div
      className="factura-info-section"
      style={{ backgroundColor, cursor }}
      onClick={hasFactura ? handleFacturaClick : undefined}
    >
      <div className="factura-info-header">
        <h4>Factura</h4>
      </div>
      <div className="factura-info-content">
        {hasFactura ? (
          <>
            <div className="factura-info-item">
              <label>Factura ID</label>
              <p>{formatFacturaId(route.factura.facturaId)}</p>
            </div>
            <div className="factura-info-item">
              <label>Payment Status</label>
              <Tag
                color={getStatusColor(route.factura.status)}
                style={{ textTransform: "capitalize" }}
              >
                {route.factura.status}
              </Tag>
            </div>
          </>
        ) : (
          <div className="factura-info-item">
            <p className="unassigned-text">Pending / Unassigned</p>
            {isAdmin && (
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%", marginTop: 12 }}
              >
                <Button
                  type="primary"
                  size="small"
                  block
                  loading={creatingNew}
                  onClick={handleNewFactura}
                >
                  New Factura
                </Button>
                <Button
                  type="default"
                  size="small"
                  block
                  loading={loading}
                  onClick={handleShowAddToFactura}
                >
                  Add to Factura
                </Button>
              </Space>
            )}
          </div>
        )}
      </div>

      {/* Add to Factura Modal */}
      <Modal
        title="Add Route to Existing Factura"
        open={showAddToFacturaModal}
        onOk={handleAddToExistingFactura}
        onCancel={handleModalCancel}
        confirmLoading={loading}
        okText="Add to Factura"
        okButtonProps={{ disabled: !selectedFacturaId }}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Select an unpaid factura to add this route to:</p>
        </div>
        <Select
          placeholder="Select a factura"
          style={{ width: "100%" }}
          value={selectedFacturaId}
          onChange={setSelectedFacturaId}
          showSearch
          optionFilterProp="children"
        >
          {unpaidFacturas.map((factura) => (
            <Option key={factura.id} value={factura.id}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
                  {factura.facturaId}
                </span>
                <span style={{ color: "#666", fontSize: "12px" }}>
                  ${(Number(factura.total) || 0).toFixed(2)}
                </span>
              </div>
            </Option>
          ))}
        </Select>
        {unpaidFacturas.length === 0 && (
          <div style={{ textAlign: "center", color: "#999", marginTop: 16 }}>
            No unpaid facturas available
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FacturaInfo;
